import React, { ReactNode, ReactElement, MouseEvent } from 'react';
import classNames from 'classnames';
import { Colors } from '../../constants/styles';
import arrowImg from './images/arrow@3x.png';
import styles from './index.module.scss';

export interface Props {
  /** 左侧内容 */
  title?: ReactNode;
  /** 左侧辅助说明内容 */
  subtitle?: ReactNode;
  /** 右侧内容 */
  extra?: ReactNode;
  /** 右侧内容占位符，仅在 extra 没有值的时候生效 */
  placeholder?: string;
  /** 右侧箭头方向 */
  arrow?: 'right' | 'up' | 'down';
  /** 子节点 */
  children?: ReactNode;
  /** 样式名称 */
  className?: string;
  /** 右侧内容颜色 */
  extraColor?: string;
  /** 是否显示左侧内容后的冒号 */
  colon?: boolean;
  /** extra 部分是否显示多行 */
  extraMultipleLine?: boolean;
  /** 是否显示底部横线 */
  borderBottom?: boolean;
  /** 点击 item 后的回调函数 */
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
}

function ListItem(props: Props): ReactElement {
  const {
    title,
    children,
    arrow,
    className,
    extra,
    extraColor,
    onClick,
    colon = true,
    placeholder,
    extraMultipleLine = false,
    borderBottom = false,
    subtitle,
  } = props;

  const itemCls = classNames(styles.listItemClass, className, {
    [styles.listItemLineClass]: !children,
    [styles.listItemArrow]: arrow,
    [styles.listItemBorderBottom]: borderBottom,
  });

  const titleCls = classNames(styles.listItemTitleClass, {
    [styles.listItemTitleWithColon]: colon,
  });

  const arrowCls = classNames(styles.arrow);

  const handleClick = (e: MouseEvent<HTMLDivElement>): void => {
    if (onClick) {
      onClick(e);
    }
  };

  const renderTitle = (): ReactNode => {
    if (!title) {
      return null;
    }

    return (
      <div className={styles.listItemTitleWrapper}>
        <div className={titleCls}>{title}</div>
        {subtitle && <div className={styles.listItemSubtitle}>{subtitle}</div>}
      </div>
    );
  };

  const renderExtra = (): ReactNode => {
    const hasExtra = !(extra === null || extra === undefined);
    // 兼容antd
    const shouldShowPlaceholder =
      (!hasExtra && placeholder) || extra === placeholder;
    const extraContent = hasExtra ? extra : placeholder;

    if (extraContent === null || extraContent === undefined) {
      return null;
    }

    const fontColor = shouldShowPlaceholder
      ? Colors.FontPlaceholder
      : extraColor;

    const extraCls = classNames(styles.listItemExtraClass, {
      [styles.singleLine]: !extraMultipleLine,
    });

    return (
      <div className={extraCls} style={{ color: fontColor }}>
        {extraContent}
      </div>
    );
  };

  return (
    <div className={itemCls} onClick={handleClick}>
      {children || (
        <>
          {renderTitle()}
          {renderExtra()}
          {arrow && <img className={arrowCls} src={arrowImg} alt="arrow" />}
        </>
      )}
    </div>
  );
}

export default ListItem;
